import { useId, type SVGProps } from 'react';
import { DOT_RADIUS, DOTS, MARK_HEIGHT, MARK_LEFT, MARK_TOP, markGeometry, NOTCH_RADIUS } from './shipcastMark';

/**
 * - `lockup`: mark + "shipcast" wordmark (optionally the tagline beneath it)
 * - `mark`: the mark alone, at its full proportions
 * - `compact`: the mark with shortened lanes, for icons rendered at 16–48 px
 */
export type ShipcastLogoVariant = 'lockup' | 'mark' | 'compact';

/** Product-specific mark; colour and typography come from the shared design system. */
export interface ShipcastLogoProps extends SVGProps<SVGSVGElement> {
  variant?: ShipcastLogoVariant;
  /** Adds "DELIVERY FORECASTING" under the wordmark. Ignored unless `variant` is `lockup`. */
  showTagline?: boolean;
}

/** Wordmark placement, measured on the reference sheet in the mark's units. */
const WORDMARK_X = 740;
const WORDMARK_WIDTH = 722;

/** The design system has no blue between primary and info, so the middle lane mixes them. */
const LANE_BLUE = 'color-mix(in oklab, var(--color-primary-600) 55%, var(--color-feedback-info))';
/** Accent pulled towards the text colour: a deep violet on light, a pale one on dark. */
const FORECAST_LINE = 'color-mix(in oklab, var(--color-text-accent) 65%, var(--color-text-primary))';
/** The cyan pill lightens towards its rounded end rather than fading out. */
const CYAN_TINT = 'color-mix(in oklab, var(--color-feedback-info) 55%, white)';

export function ShipcastLogo({ variant = 'lockup', showTagline = false, ...props }: ShipcastLogoProps) {
  const id = useId().replace(/:/g, '');
  const mark = markGeometry(variant === 'compact');
  const right = variant === 'lockup' ? WORDMARK_X + WORDMARK_WIDTH + 2 : mark.right;
  const { stops } = mark;
  // userSpaceOnUse: every layer shares one x axis, so the fades line up under the bell.
  const across = { gradientUnits: 'userSpaceOnUse', x1: 0, y1: 0, x2: mark.right, y2: 0 } as const;
  const notches = DOTS.map(({ cx, cy }) => <circle key={cy} cx={cx} cy={cy} r={NOTCH_RADIUS} fill="black" />);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`${MARK_LEFT} ${MARK_TOP} ${right - MARK_LEFT} ${MARK_HEIGHT}`}
      role="img"
      aria-label={showTagline && variant === 'lockup' ? 'Shipcast — delivery forecasting' : 'Shipcast'}
      {...props}
    >
      <defs>
        <linearGradient id={`${id}-body`} {...across}>
          <stop offset={stops.bodyStart} stopColor="var(--color-primary-500)" stopOpacity="0" />
          <stop offset={stops.bodyMid} stopColor="var(--color-primary-500)" stopOpacity=".12" />
          <stop offset={stops.bell} stopColor="var(--color-primary-500)" stopOpacity=".4" />
          <stop offset="1" stopColor="var(--color-primary-500)" stopOpacity=".12" />
        </linearGradient>
        <linearGradient id={`${id}-top`} {...across}>
          <stop stopColor="var(--color-primary-600)" />
          <stop offset={stops.laneBend} stopColor="var(--color-primary-500)" />
          <stop offset={stops.bell} stopColor="var(--color-primary-500)" stopOpacity=".6" />
          <stop offset={stops.bellEdge} stopColor="var(--color-primary-500)" stopOpacity=".4" />
        </linearGradient>
        <linearGradient id={`${id}-middle`} {...across}>
          <stop stopColor={LANE_BLUE} />
          <stop offset={stops.middleSolid} stopColor={LANE_BLUE} />
          <stop offset={stops.middleFade} stopColor={LANE_BLUE} stopOpacity=".55" />
          <stop offset={stops.bell} stopColor="var(--color-feedback-info)" stopOpacity=".25" />
          <stop offset="1" stopColor="var(--color-feedback-info)" stopOpacity=".1" />
        </linearGradient>
        <linearGradient id={`${id}-cyan-bar`} {...across}>
          <stop offset={stops.cyanBarSolid} stopColor="var(--color-feedback-info)" />
          <stop offset={stops.cyanBarEnd} stopColor={CYAN_TINT} />
        </linearGradient>
        <linearGradient id={`${id}-cyan-wave`} {...across}>
          <stop offset={stops.waveStart} stopColor="var(--color-feedback-info)" stopOpacity=".1" />
          <stop offset={stops.waveIn} stopColor="var(--color-feedback-info)" stopOpacity=".4" />
          <stop offset={stops.waveOut} stopColor="var(--color-feedback-info)" stopOpacity=".35" />
          <stop offset={stops.waveFade} stopColor="var(--color-feedback-info)" stopOpacity=".15" />
          <stop offset="1" stopColor="var(--color-feedback-info)" stopOpacity=".05" />
        </linearGradient>
        <linearGradient id={`${id}-line`} gradientUnits="userSpaceOnUse" x1="0" y1="14" x2="0" y2="265">
          <stop stopColor={FORECAST_LINE} />
          <stop offset="1" stopColor="var(--color-feedback-info)" />
        </linearGradient>
        {/* Cuts the concave notch where each bar meets its dot; transparent, so it works on any background. */}
        <mask
          id={`${id}-notches`}
          maskUnits="userSpaceOnUse"
          x={MARK_LEFT}
          y={MARK_TOP}
          width={mark.right}
          height={MARK_HEIGHT}
        >
          <rect x={MARK_LEFT} y={MARK_TOP} width={mark.right} height={MARK_HEIGHT} fill="white" />
          {notches}
        </mask>
        <filter
          id={`${id}-soften`}
          filterUnits="userSpaceOnUse"
          x={MARK_LEFT - 60}
          y={MARK_TOP - 60}
          width={mark.right + 120}
          height={MARK_HEIGHT + 120}
        >
          <feGaussianBlur stdDeviation="7" />
        </filter>
        {/* The top lane also fades into the bell instead of ending on a hard edge. */}
        <mask
          id={`${id}-top-lane`}
          maskUnits="userSpaceOnUse"
          x={MARK_LEFT}
          y={MARK_TOP}
          width={mark.right}
          height={MARK_HEIGHT}
        >
          <path d={mark.topFade} fill="white" filter={`url(#${id}-soften)`} />
          <rect x={MARK_LEFT} y={MARK_TOP} width={mark.topFadeFrom - MARK_LEFT} height={MARK_HEIGHT} fill="white" />
          {notches}
        </mask>
      </defs>
      <path d={mark.body} fill={`url(#${id}-body)`} />
      <path d={mark.cyanWave} fill={`url(#${id}-cyan-wave)`} />
      <g mask={`url(#${id}-notches)`}>
        <path d={mark.middle} fill={`url(#${id}-middle)`} />
        <path d={mark.cyanBar} fill={`url(#${id}-cyan-bar)`} />
      </g>
      <path d={mark.top} fill={`url(#${id}-top)`} mask={`url(#${id}-top-lane)`} />
      <path
        d={mark.line}
        stroke={`url(#${id}-line)`}
        strokeWidth={mark.lineWidth}
        strokeDasharray={mark.dash}
        strokeLinecap="round"
      />
      <circle cx={DOTS[0].cx} cy={DOTS[0].cy} r={DOT_RADIUS} fill="var(--color-primary-600)" />
      <circle cx={DOTS[1].cx} cy={DOTS[1].cy} r={DOT_RADIUS} fill={LANE_BLUE} />
      <circle cx={DOTS[2].cx} cy={DOTS[2].cy} r={DOT_RADIUS} fill="var(--color-feedback-info)" />
      {variant === 'lockup' && (
        <g fontFamily="var(--font-sans)" fontWeight="800">
          <text
            x={WORDMARK_X}
            y={182}
            fontSize="186"
            textLength={WORDMARK_WIDTH}
            lengthAdjust="spacingAndGlyphs"
            fill="var(--color-text-primary)"
          >
            ship<tspan fill="var(--color-text-accent)">cast</tspan>
          </text>
          {showTagline && (
            <text
              x={WORDMARK_X + 8}
              y="250"
              fontSize="38"
              fontWeight="600"
              textLength={WORDMARK_WIDTH - 10}
              lengthAdjust="spacing"
              fill="var(--color-text-muted)"
            >
              DELIVERY FORECASTING
            </text>
          )}
        </g>
      )}
    </svg>
  );
}
